import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { getToken, getUserId } from '../utils/auth';
import { API_BASE } from './client';
import { logger } from '../utils/logger';

const log = logger.scope('Realtime');

const DISABLE_REALTIME = !!import.meta.env.VITE_DISABLE_REALTIME;

// Generate a unique window ID that persists for this browser tab
function getWindowId(): string {
  let windowId = sessionStorage.getItem('neuro-window-id');
  if (!windowId) {
    windowId = `window-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('neuro-window-id', windowId);
  }
  return windowId;
}

/**
 * Real-time entity change event.
 *
 * entityType is a plain string so plugins can register
 * their own entity types at runtime.
 */
export interface EntityChangeEvent {
  entityType: string;
  action: 'created' | 'updated' | 'deleted';
  entityId: string;
  username: string;
  timestamp: number;
  data?: any;
}

export interface EditLock {
  entityType: string;
  entityId: string;
  username: string;
  windowId: string;
  lockedAt: number;
}

interface RealtimeContextValue {
  socket: Socket | null;
  connected: boolean;
  windowId: string;
  
  // Lock management
  requestLock: (entityType: string, entityId: string) => Promise<{ success: boolean; existingLock?: EditLock }>;
  releaseLock: (entityType: string, entityId: string) => void;
  checkLock: (entityType: string, entityId: string) => Promise<{ locked: boolean; lock?: EditLock }>;
  refreshLock: (entityType: string, entityId: string) => void;
  
  // Event subscriptions
  onEntityChange: (callback: (event: EntityChangeEvent) => void) => () => void;
  onLockAcquired: (callback: (lock: EditLock) => void) => () => void;
  onLockReleased: (callback: (data: { entityType: string; entityId: string }) => void) => () => void;
  
  // Track changes to avoid reacting to our own updates
  trackChange: (entityType: string, entityId: string) => void;
  
  // Get current locks held by others
  getActiveLocks: () => Promise<EditLock[]>;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const windowId = useRef(getWindowId()).current;
  const entityChangeCallbacks = useRef<Set<(event: EntityChangeEvent) => void>>(new Set());
  const lockAcquiredCallbacks = useRef<Set<(lock: EditLock) => void>>(new Set());
  const lockReleasedCallbacks = useRef<Set<(data: { entityType: string; entityId: string }) => void>>(new Set());
  
  // Track recent changes to avoid reacting to our own updates
  const recentChanges = useRef<Map<string, number>>(new Map());
  
  // Method to track that we made a change
  const trackChange = useCallback((entityType: string, entityId: string) => {
    const key = `${entityType}:${entityId}`;
    recentChanges.current.set(key, Date.now());
    
    // Clean up old entries after 5 seconds
    setTimeout(() => {
      recentChanges.current.delete(key);
    }, 5000);
  }, []);
  
  useEffect(() => {
    const token = getToken();
    const username = getUserId();

    if (!token || !username) {
      log.log(' No auth token, skipping socket connection');
      return;
    }

    if (DISABLE_REALTIME) {
      log.log(' Realtime disabled via VITE_DISABLE_REALTIME; skipping socket connection');
      return;
    }

    log.log(' Connecting to realtime service...');
    const newSocket = io(`${API_BASE}/realtime`, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      auth: { token },
    });

    newSocket.on('connect', () => {
      log.log(' Connected to realtime service');
      setConnected(true);
      
      // Register this window
      newSocket.emit('register', { username, windowId });
    });

    newSocket.on('disconnect', () => {
      log.log(' Disconnected from realtime service');
      setConnected(false);
    });

    newSocket.on('entityChange', (event: EntityChangeEvent) => {
      const changeKey = `${event.entityType}:${event.entityId}`;
      const lastChangeTime = recentChanges.current.get(changeKey);
      
      // Ignore events for changes we made recently (within last 5 seconds)
      if (lastChangeTime && (Date.now() - lastChangeTime) < 5000) {
        log.log(' Ignoring recent change event:', event);
        return;
      }
      
      log.log(' Entity change received:', event);
      entityChangeCallbacks.current.forEach(cb => cb(event));
    });

    newSocket.on('lockAcquired', (lock: EditLock) => {
      log.log(' Lock acquired by another window:', lock);
      // Only notify if it's not our own window
      if (lock.windowId !== windowId) {
        lockAcquiredCallbacks.current.forEach(cb => cb(lock));
      }
    });

    newSocket.on('lockReleased', (data: { entityType: string; entityId: string }) => {
      log.log(' Lock released:', data);
      lockReleasedCallbacks.current.forEach(cb => cb(data));
    });

    newSocket.on('connect_error', (error) => {
      log.error(' Connection error:', error);
    });

    setSocket(newSocket);

    return () => {
      log.log(' Cleaning up socket connection');
      newSocket.close();
    };
  }, [windowId]);

  const requestLock = useCallback(async (entityType: string, entityId: string): Promise<{ success: boolean; existingLock?: EditLock }> => {
    if (!socket || !connected) {
      log.warn(' Cannot request lock - not connected');
      return { success: false }; // Deny lock when offline — don't silently allow edits
    }

    const username = getUserId();
    if (!username) return { success: false };

    return new Promise((resolve) => {
      socket.emit('requestLock', { entityType, entityId, username, windowId }, (response: { success: boolean; existingLock?: EditLock }) => {
        resolve(response);
      });
    });
  }, [socket, connected, windowId]);

  const releaseLock = useCallback((entityType: string, entityId: string) => {
    if (!socket || !connected) return;

    const username = getUserId();
    if (!username) return;

    socket.emit('releaseLock', { entityType, entityId, username, windowId });
  }, [socket, connected, windowId]);

  const checkLock = useCallback(async (entityType: string, entityId: string): Promise<{ locked: boolean; lock?: EditLock }> => {
    if (!socket || !connected) {
      return { locked: false };
    }

    return new Promise((resolve) => {
      socket.emit('checkLock', { entityType, entityId }, (response: { locked: boolean; lock?: EditLock }) => {
        // Don't consider it locked if we hold the lock
        if (response.locked && response.lock?.windowId === windowId) {
          resolve({ locked: false });
        } else {
          resolve(response);
        }
      });
    });
  }, [socket, connected, windowId]);

  const refreshLock = useCallback((entityType: string, entityId: string) => {
    if (!socket || !connected) return;

    const username = getUserId();
    if (!username) return;

    socket.emit('refreshLock', { entityType, entityId, username, windowId });
  }, [socket, connected, windowId]);

  const onEntityChange = useCallback((callback: (event: EntityChangeEvent) => void) => {
    entityChangeCallbacks.current.add(callback);
    return () => {
      entityChangeCallbacks.current.delete(callback);
    };
  }, []);

  const onLockAcquired = useCallback((callback: (lock: EditLock) => void) => {
    lockAcquiredCallbacks.current.add(callback);
    return () => {
      lockAcquiredCallbacks.current.delete(callback);
    };
  }, []);

  const onLockReleased = useCallback((callback: (data: { entityType: string; entityId: string }) => void) => {
    lockReleasedCallbacks.current.add(callback);
    return () => {
      lockReleasedCallbacks.current.delete(callback);
    };
  }, []);

  const getActiveLocks = useCallback(async (): Promise<EditLock[]> => {
    if (!socket || !connected) return [];

    const username = getUserId();
    if (!username) return [];

    return new Promise((resolve) => {
      socket.emit('getActiveLocks', { username }, (response: { locks: EditLock[] }) => {
        // Filter out our own locks
        resolve(response.locks.filter(lock => lock.windowId !== windowId));
      });
    });
  }, [socket, connected, windowId]);

  const value: RealtimeContextValue = {
    socket,
    connected,
    windowId,
    requestLock,
    releaseLock,
    checkLock,
    refreshLock,
    onEntityChange,
    onLockAcquired,
    onLockReleased,
    trackChange,
    getActiveLocks,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
}

// Hook for subscribing to entity changes
export function useEntityChanges(
  entityType: EntityChangeEvent['entityType'] | EntityChangeEvent['entityType'][],
  callback: (event: EntityChangeEvent) => void
) {
  const { onEntityChange } = useRealtime();
  
  useEffect(() => {
    const types = Array.isArray(entityType) ? entityType : [entityType];
    
    const unsubscribe = onEntityChange((event) => {
      if (types.includes(event.entityType)) {
        callback(event);
      }
    });

    return unsubscribe;
  }, [entityType, callback, onEntityChange]);
}

// Hook for managing edit locks
export function useEditLock(entityType: string, entityId: string | null) {
  const { requestLock, releaseLock, checkLock, refreshLock, onLockAcquired, onLockReleased, windowId } = useRealtime();
  const [isLocked, setIsLocked] = useState(false);
  const [lockHolder, setLockHolder] = useState<EditLock | null>(null);
  const [hasLock, setHasLock] = useState(false);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check lock status when entity changes
  useEffect(() => {
    if (!entityId) {
      setIsLocked(false);
      setLockHolder(null);
      setHasLock(false);
      return;
    }

    checkLock(entityType, entityId).then(({ locked, lock }) => {
      setIsLocked(locked);
      setLockHolder(lock || null);
    });
  }, [entityType, entityId, checkLock]);

  // Listen for lock changes from other windows
  useEffect(() => {
    const unsubAcquired = onLockAcquired((lock) => {
      if (lock.entityType === entityType && lock.entityId === entityId) {
        setIsLocked(true);
        setLockHolder(lock);
      }
    });

    const unsubReleased = onLockReleased((data) => {
      if (data.entityType === entityType && data.entityId === entityId) {
        setIsLocked(false);
        setLockHolder(null);
      }
    });

    return () => {
      unsubAcquired();
      unsubReleased();
    };
  }, [entityType, entityId, onLockAcquired, onLockReleased]);

  const acquireLock = useCallback(async (): Promise<boolean> => {
    if (!entityId) return false;

    const result = await requestLock(entityType, entityId);
    if (result.success) {
      setHasLock(true);
      setIsLocked(false);
      setLockHolder(null);
      
      // Start refreshing the lock every 2 minutes
      refreshIntervalRef.current = setInterval(() => {
        refreshLock(entityType, entityId);
      }, 2 * 60 * 1000);
      
      return true;
    } else {
      setIsLocked(true);
      setLockHolder(result.existingLock || null);
      return false;
    }
  }, [entityType, entityId, requestLock, refreshLock]);

  const release = useCallback(() => {
    if (!entityId) return;
    
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
    
    releaseLock(entityType, entityId);
    setHasLock(false);
  }, [entityType, entityId, releaseLock]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (hasLock && entityId) {
        releaseLock(entityType, entityId);
      }
    };
  }, [entityType, entityId, hasLock, releaseLock]);

  return {
    isLocked,
    lockHolder,
    hasLock,
    acquireLock,
    releaseLock: release,
  };
}
